import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import { withTimeout } from '../../shared/withTimeout';

import {
	SAPClaimSocialStarterPetitionAngles,
	type SapClaimSocialStarterPetitionAnglesMutation,
	type SapClaimSocialStarterPetitionAnglesMutationVariables,
} from './claimSocialStarterPetitionAngles.graphql';

type Params = SapClaimSocialStarterPetitionAnglesMutationVariables['input'] & { utilityContext: UtilityContext };

// The caller awaits this before navigating to starter onboarding (to avoid racing the server-side
// staffing it triggers), so a hung request must reject rather than block publish indefinitely.
const CLAIM_TIMEOUT_MS = 8000;

/**
 * Claims the social starter petition angles for the given token, best-effort linking them to the
 * already-created petition; failures are only logged, never surfaced or blocking.
 */
export async function claimSocialStarterPetitionAngles({
	token,
	petitionId,
	clearSourceUrl,
	utilityContext,
}: Params): Promise<void> {
	const {
		errorReporter,
		gql: { fetch },
	} = utilityContext;

	try {
		const { data } = await withTimeout(
			fetch<SapClaimSocialStarterPetitionAnglesMutation, SapClaimSocialStarterPetitionAnglesMutationVariables>({
				query: SAPClaimSocialStarterPetitionAngles,
				variables: { input: { token, petitionId, clearSourceUrl } },
				rejectOnError: true,
			}),
			CLAIM_TIMEOUT_MS,
			`Claim social starter petition angle timed out after ${CLAIM_TIMEOUT_MS}ms`,
		);

		const { success } = data.claimSocialStarterPetitionAngles;
		if (!success) {
			throw new Error('Failed to claim social starter petition angle');
		}
	} catch (error) {
		void errorReporter.report({
			error: error instanceof Error ? error : new Error('Failed to claim social starter petition angle'),
		});
	}
}
